<template>
  <div class="auth-form">
    <p class="auth-form__legend">Please complete all fields.</p>
    <form @submit.prevent="register">
      <slot
        name="email"
        :email="email"
        :errors="errors.email"
        :emailBlurCheck="emailBlurCheck"
        :emailInputCheck="emailInputCheck"
        :update="update"
      >
        <label for="email" class="auth-form__label">
          Email
          <input
            id="email"
            ref="email"
            v-model="email"
            type="text"
            class="auth-form__input"
            required
            aria-required="true"
            :aria-invalid="!errors.email.length"
            @blur="emailBlurCheck"
            @input="emailInputCheck"
          />
        </label>
      </slot>
      <span class="auth-form__error" id="emailError" role="alert" v-if="errors.email.length">
        <div v-for="error in errors.email" :key="error">
          <span class="sr-only">Error:</span>{{ error }}
        </div>
      </span>

      <label class="auth-form__label">
        <input type="checkbox" class="auth-form__simple" v-model="allowPersonalEmail" />
        I'm registering as an individual
      </label>

      <div v-if="allowPersonalEmail" class="auth-form__personal-email-info">
        <p class="auth-form__legend">
          We designed this platform to help teams and organizations unlock insights at scale.
          <br /><br />
          If you're an individual researcher or small business, we’re still happy to support you —
          we just ask for a brief description of your intended work so we can ensure you get the
          right experience.
        </p>

        <label for="personalEmailExplanation" class="auth-form__longLabel">
          Tell us about how you intend to use Research AI
          <textarea
            id="personalEmailExplanation"
            v-model="personalEmailExplanation"
            class="auth-form__input"
            aria-required="true"
            :aria-invalid="!!errors.personalEmailExplanation.length"
          ></textarea>
        </label>
        <span class="auth-form__error" role="alert" v-if="errors.personalEmailExplanation.length">
          <div v-for="error in errors.personalEmailExplanation" :key="error">
            <span class="sr-only">Error:</span>{{ error }}
          </div>
        </span>
      </div>

      <slot
        name="password"
        :password="password"
        :errors="errors"
        :passwordInputCheck="passwordInputCheck"
        :passwordBlurCheck="passwordBlurCheck"
        :update="update"
        :email="email"
      >
        <label for="password" class="auth-form__label" v-if="canShowPasswordSection">
          Passwords
          <input
            id="password"
            ref="password"
            v-model="password"
            type="password"
            class="auth-form__input"
            required
            aria-required="true"
            :aria-invalid="!!errors.password.length"
            aria-describedby="password-desc"
            @input="passwordInputCheck"
            @blur="passwordBlurCheck"
          />
          <span
            id="password-desc"
            v-if="!errors.password.includes('Passwords should be at least 10 characters long')"
          >
            Passwords should be at least 10 characters long
          </span>
        </label>
      </slot>
      <span class="auth-form__error" id="emailError" role="alert" v-if="errors.password.length">
        <div v-for="error in errors.password" :key="error">
          <span class="sr-only">Error:</span>{{ error }}
        </div>
      </span>

      <slot
        name="passwordConf"
        :passwordConf="passwordConf"
        :errors="errors"
        :passwordConfBlurCheck="passwordConfBlurCheck"
        :update="update"
        :email="email"
      >
        <label for="passwordConf" class="auth-form__label" v-if="canShowPasswordSection">
          Confirm Password
          <input
            id="passwordConf"
            ref="passwordConf"
            v-model="passwordConf"
            type="password"
            class="auth-form__input"
            required
            aria-required="true"
            :aria-invalid="!!errors.passwordConf.length"
            @blur="passwordConfBlurCheck"
          />
        </label>
      </slot>
      <span class="auth-form__error" id="emailError" role="alert" v-if="errors.passwordConf.length">
        <div v-for="error in errors.passwordConf" :key="error">
          <span class="sr-only">Error:</span>{{ error }}
        </div>
      </span>

      <button
        v-if="canShowPasswordSection"
        class="auth-form__input"
        :disabled="isLoading"
        type="submit"
      >
        Register
      </button>
      <span v-if="isLoading" class="also-found__loading-spinner spinner"></span>
    </form>
  </div>
</template>

<script>
// NOTE this version of register is not used. We primarily moved to RegisterSmall in 1.11.1. TODO if so remove this?
//import RegisterSmall from '../../../components/Register/RegisterSmall.vue';
import Validator from '../../lib/password/registerFormValidatior';

export default {
  name: 'register',
  props: {
    inviteId: {
      default: () => '',
      type: String,
    },
  },
  data() {
    return {
      email: '',
      password: '',
      passwordConf: '',
      submitted: false,
      validator: null,
      message: null,
      errors: {
        email: [],
        password: [],
        passwordConf: [],
        personalEmailExplanation: [],
      },
      isLoading: false,
      hasInviteId: false,
      allowPersonalEmail: false,
      personalEmailExplanation: '',
    };
  },
  mounted() {
    this.validator = new Validator(this.$data);
    if (this.inviteId !== '') {
      this.hasInviteId = true;
    }
  },
  computed: {
    canShowPasswordSection() {
      const emailNotEmpty = this.email && this.email.trim().length > 0;
      if (!emailNotEmpty) return false;

      const emailErrors = this.errors.email;

      // Must not have the "invalid email" error
      const hasInvalidEmailError = emailErrors.some((e) => e.includes('valid email'));
      if (hasInvalidEmailError) return false;

      // CASE A — Business email path
      if (!this.allowPersonalEmail) {
        return emailErrors.length === 0;
      }

      // CASE B — Personal email path
      const explanationValid =
        this.personalEmailExplanation.trim().length > 0 &&
        this.errors.personalEmailExplanation.length === 0;

      // Personal path allows:
      // - no email errors (business email + checkbox)
      // - OR one personal-email warning
      const isEmailAcceptableForPersonal =
        emailErrors.length === 0 ||
        (emailErrors.length === 1 && emailErrors[0].includes('personal email'));

      return isEmailAcceptableForPersonal && explanationValid;
    },
  },

  methods: {
    async register() {
      try {
        this.submitted = true;
        if (!this.validateForm()) return;
        this.isLoading = true;
        const user = await this.$pigeonline.register(
          this.email,
          this.password,
          this.hasInviteId,
          this.personalEmailExplanation,
        );
        this.isLoading = false;
        this.$emit('registered', user);
      } catch (e) {
        //console.error(e);
        this.isLoading = false;
        const data = e.response.data;

        Object.keys(this.errors).forEach((key) => {
          if (key in data) {
            this.errors[key] = data[key];
          }
        });

        this.message = 'There was an error performing registration. Please try again.';
        this.$emit('error');
      }
    },
    emailBlurCheck() {
      const errs = this.validator.validate('email', 'blur');

      if (this.allowPersonalEmail) {
        this.errors.email = errs.filter((e) => !e.includes('personal email'));
      } else {
        this.errors.email = errs;
      }
    },
    passwordInputCheck() {
      this.errors.password = this.validator.validate('password', 'input');
    },
    passwordBlurCheck() {
      this.errors.password = this.validator.validate('password', 'blur');
    },
    passwordConfBlurCheck() {
      this.errors.passwordConf = this.validator.validate('passwordConf', 'blur');
    },
    emailInputCheck() {
      const errs = this.validator.validate('email', 'blur');

      if (this.allowPersonalEmail) {
        this.errors.email = errs.filter((e) => !e.includes('personal email'));
      } else {
        this.errors.email = errs;
      }
    },

    validateForm() {
      ['email', 'password', 'passwordConf', 'personalEmailExplanation'].forEach((field) => {
        let errs = this.validator.validate(field, 'submit');

        if (field === 'email' && this.allowPersonalEmail) {
          errs = errs.filter((e) => !e.includes('personal email'));
        }

        this.errors[field] = errs;
      });

      return Object.values(this.errors).flat().length === 0;
    },

    update(field, val) {
      this[field] = val;
    },
    /**
     * Set focus to input field with id = key
     * This is to address chrome bug where pressing enter submits form insead
     * of setting focus to input field.
     * This is also to address firefox bug that does not give focus on click.
     */
    setFocus(key) {
      this.$refs[key].focus();
    },
  },
};
</script>

<style src="./auth-styles.css"></style>
